import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import styles from "./RatingsAndReviews.scss";
import { StarRatingGroup } from "../ratingComponents";
import Review from "../review/Review";
import Recommended from "../recommended/Recommended";
import ReviewsSummary from "../reviewsSummary/ReviewsSummary";
import SubRatings from "../subRatings/SubRatings";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import ExpandableText, { textStyles } from "../expandableText/ExpandableText";
import { REVIEW_DISCLAIMER_STORES } from "../../constants";
import ReviewPolicyButton from "../reviewPolicyButton/ReviewPolicyButton";

function displayDisclaimer(storeId) {
  return REVIEW_DISCLAIMER_STORES.includes(storeId);
}

export default function RatingsAndReviews({
  ratings,
  language,
  openRatingsModal,
  handleReportSuccess,
}) {
  const {
    averageOverallStarRating,
    averageOverallRating,
    totalReviewCount,
    percentageRecommended,
    mostRecent,
    lastThree,
    subRatings,
    reviewsSummary,
  } = ratings;

  const {
    getTranslation,
    webContext: { storeId },
  } = useRuntimeConstants();

  const [leftStickyHeaderHeight, setLeftStickyHeaderHeight] = useState(0);
  const [rightStickyHeaderHeight, setRightStickyHeaderHeight] = useState(0);
  const leftHeaderRef = useRef(null);
  const rightHeaderRef = useRef(null);

  useEffect(() => {
    const observers = [];
    if (leftHeaderRef.current) {
      const obs = new ResizeObserver(() => {
        if (leftHeaderRef.current) {
          setLeftStickyHeaderHeight(leftHeaderRef.current.offsetHeight);
        }
      });
      obs.observe(leftHeaderRef.current);
      observers.push(obs);
    }
    if (rightHeaderRef.current) {
      const obs = new ResizeObserver(() => {
        if (rightHeaderRef.current) {
          setRightStickyHeaderHeight(rightHeaderRef.current.offsetHeight);
        }
      });
      obs.observe(rightHeaderRef.current);
      observers.push(obs);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  function getReviewKey(index, review) {
    if (review.title) {
      return `${index}-${review.title.slice(0, 10)}`;
    }
    if (review.reviewText) {
      return `${index}-${review.reviewText.slice(0, 10)}`;
    }
    return index;
  }

  const isSupportedStore = (store) =>
    store === "AU" ||
    store === "COM" ||
    store === "US" ||
    store === "ROW" ||
    store === "ROE";

  const mostRecentReviewTitle = isSupportedStore(storeId)
    ? getTranslation("pdp_ratings_reviews_most_recent_title_plural")
    : getTranslation("pdp_ratings_reviews_most_recent_title");

  return (
    <section className={styles.sectionContainer} aria-labelledby="reviewTitle">
      <div className={styles.maxWidthContainer} data-testid="reviewsSection">
        <h2 className={styles.title} id="reviewTitle" tabIndex={-1}>
          {getTranslation("pdp_ratings_reviews_reviews_title")}
        </h2>
        <div
          className={styles.leftSubContainer}
          style={{ height: `${leftStickyHeaderHeight}px` }}
        >
          <div ref={leftHeaderRef}>
            <div className={styles.customerRatings}>
              <StarRatingGroup
                averageOverallStarRating={averageOverallStarRating}
                averageOverallRating={averageOverallRating}
                totalReviewCount={totalReviewCount}
                language={language}
              />
              <Recommended
                percentageRecommended={percentageRecommended}
                language={language}
              />
              <ReviewPolicyButton />
            </div>
            {reviewsSummary && (
              <div className={styles.reviewsSummary}>
                <ReviewsSummary reviewsSummary={reviewsSummary} />
              </div>
            )}
            <div className={styles.customerReviews}>
              <SubRatings subRatings={subRatings} />
            </div>
          </div>
        </div>
        <div
          className={styles.rightSubContainer}
          style={{ height: `${rightStickyHeaderHeight}px` }}
        >
          <div ref={rightHeaderRef}>
            <div className={styles.customerReviews}>
              <h3 className={styles.mostRecentReviewTitle}>
                {mostRecentReviewTitle}
              </h3>
              {isSupportedStore(storeId) ? (
                lastThree.map((review, index) => (
                  <div
                    className={styles.reviewContainer}
                    key={getReviewKey(index, review)}
                  >
                    <Review
                      {...review}
                      onReportSuccess={handleReportSuccess}
                      reviewIsInModal={false}
                    />
                  </div>
                ))
              ) : (
                <Review
                  {...mostRecent}
                  onReportSuccess={handleReportSuccess}
                  reviewIsInModal={false}
                />
              )}

              <button
                className={styles.viewAllReviews}
                onClick={openRatingsModal}
                data-testid="reviewsViewAll"
              >
                {getTranslation("pdp_ratings_reviews_view_all_reviews_cta")}
              </button>
              {displayDisclaimer(storeId) && (
                <ExpandableText
                  testId="reviews-disclaimer"
                  readMoreTranslationKey="rdp_ratings_reviews_distribution_view_more_cta"
                  text={getTranslation("pdp_ratings_review_legalblurb")}
                  textStyle={textStyles.DISCLAIMER}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

RatingsAndReviews.propTypes = {
  ratings: PropTypes.shape({
    averageOverallStarRating: PropTypes.number.isRequired,
    averageOverallRating: PropTypes.number.isRequired,
    totalReviewCount: PropTypes.number.isRequired,
    percentageRecommended: PropTypes.number.isRequired,
    reviewsSummary: PropTypes.shape({}),
    subRatings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        minLabel: PropTypes.string.isRequired,
        midLabel: PropTypes.string,
        maxLabel: PropTypes.string.isRequired,
        percentage: PropTypes.number.isRequired,
      }).isRequired,
    ).isRequired,
    mostRecent: PropTypes.shape({
      title: PropTypes.string,
      reviewText: PropTypes.string,
      rating: PropTypes.number.isRequired,
      submissionRecency: PropTypes.string.isRequired,
      syndicationSource: PropTypes.shape({ name: PropTypes.string.isRequired }),
      badgesOrder: PropTypes.array.isRequired,
      isReported: PropTypes.bool.isRequired,
    }).isRequired,
    lastThree: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        reviewText: PropTypes.string,
        rating: PropTypes.number.isRequired,
        submissionRecency: PropTypes.string.isRequired,
        syndicationSource: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }),
        badgesOrder: PropTypes.array.isRequired,
        isReported: PropTypes.bool.isRequired,
      }).isRequired,
    ).isRequired,
  }).isRequired,
  language: PropTypes.string.isRequired,
  openRatingsModal: PropTypes.func.isRequired,
  openReviewPolicyModal: PropTypes.func.isRequired,
  getReviewsCallback: PropTypes.func.isRequired,
  handleReportSuccess: PropTypes.func.isRequired,
  reviewIsInModal: PropTypes.bool,
};
